/**
 * APCA (Advanced Perceptual Contrast Algorithm) Utilities
 * 
 * Utilities for APCA contrast calculations, level determination, and conversions
 * between WCAG 2.1 ratios and APCA Lc (Lightness Contrast) values.
 * 
 * APCA is part of WCAG 3.0 and provides more perceptually accurate contrast
 * measurements than WCAG 2.1 ratios.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * APCA accessibility level based on Lc value
 * - FAIL: Below 60 Lc - Not accessible
 * - +18px: 60-75 Lc - Accessible for large text and non-body content
 * - AA: 75-90 Lc - Accessible for body text (18px+)
 * - AAA: 90+ Lc - Preferred for fluent text and columns
 */
export type APCALevel = 'FAIL' | '+18px' | 'AA' | 'AAA';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * APCA Lc thresholds based on WCAG 3.0 guidelines
 */
export const APCA_THRESHOLDS = {
  AAA: 90,    // Preferred level for fluent text and columns (14px+ normal weight)
  AA: 75,     // Minimum for columns of body text (18px+ normal weight)
  LARGE: 60,  // Minimum for non-body content and large text
  FAIL: 0,    // Below 60 fails all criteria
} as const;

/**
 * APCA Lc value ranges
 * APCA can return values from -108 to +106, but we focus on positive values
 * (dark text on light background for light themes, light text on dark for dark themes)
 */
export const APCA_RANGE = {
  MIN: 1,   // Practical minimum for UI purposes
  MAX: 108,  // Maximum Lc value
} as const;

// =============================================================================
// APCA LEVEL CALCULATION
// =============================================================================

/**
 * Determine APCA accessibility level from Lc value
 * 
 * Based on WCAG 3.0 guidelines:
 * - Level AAA requires Lc 90+ for fluent text (14px+ normal weight)
 * - Level AA requires Lc 75+ for body text (18px+ normal weight)
 * - Level +18px requires Lc 60+ for non-body content
 * - Below Lc 60 fails all criteria
 * 
 * @param lc - APCA Lightness Contrast value (15 to 106)
 * @returns APCA level string
 * 
 * @example
 * getAPCALevel(92);  // 'AAA'
 * getAPCALevel(78);  // 'AA'
 * getAPCALevel(65);  // '+18px'
 * getAPCALevel(45);  // 'FAIL'
 */
export const getAPCALevel = (lc: number): APCALevel => {
  // Use absolute value to handle negative Lc values
  const absLc = Math.abs(lc);
  
  if (absLc >= APCA_THRESHOLDS.AAA) return 'AAA';
  if (absLc >= APCA_THRESHOLDS.AA) return 'AA';
  if (absLc >= APCA_THRESHOLDS.LARGE) return '+18px';
  return 'FAIL';
};

// =============================================================================
// FORMATTING UTILITIES
// =============================================================================

/**
 * Format APCA Lc value for display
 * 
 * @param lc - APCA Lightness Contrast value
 * @returns Formatted string like "Lc 75.5" or "75.50"
 * 
 * @example
 * formatAPCAContrast(75.5);  // "75.50"
 * formatAPCAContrast(90);    // "90.00"
 */
export const formatAPCAContrast = (lc: number): string => {
  return lc.toFixed(2);
};

// NOTE: Previously had wcagToAPCA and apcaToWCAG conversion functions here.
// These are no longer needed as we now preserve colors when switching formulas
// and recalculate their contrast using Leonardo's contrast() function directly.
